import type { JSX } from 'react';

import { useNow } from '@change-corgi/core/react/date';
import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { iconDraw, iconGroup, iconLock } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { WEEKLY_SIGNATURE_THRESHOLD } from 'src/app/pages/movement/shared/constants';

import { RemoveOption } from './removeOption';

type StatusBoxProp = {
	isVictory: boolean;
	isAdmin?: boolean;
	userIsAuthenticated: boolean;
	weeklySignatureCount: number;
	isPetitionOwner: boolean | '' | undefined;
	publishedAt: string;
	onDelete: () => void;
};

export function StatusBox({
	isVictory,
	isAdmin,
	userIsAuthenticated,
	weeklySignatureCount,
	isPetitionOwner,
	publishedAt,
	onDelete,
}: StatusBoxProp): JSX.Element {
	const now = useNow();
	const { localizeRelativeTime, localizeNumber } = useI18n();

	const userCanRemoveThePetition = userIsAuthenticated && (isAdmin || isPetitionOwner);

	if (isVictory) {
		return (
			<Flex sx={{ justifyContent: 'space-between', width: '100%' }}>
				<Box
					sx={{ backgroundColor: 'primary-changeRed', borderRadius: 'standard', borderWidth: 0, width: 'fit-content' }}
					px={8}
					py={4}
				>
					<Heading size="h6" as="h5" sx={{ color: 'white' }}>
						<Translate value="corgi.movement.show.petition_card.victory" />
					</Heading>
				</Box>
				{userCanRemoveThePetition && <RemoveOption onDelete={onDelete} />}
			</Flex>
		);
	}
	if (isAdmin) {
		return (
			<Flex sx={{ justifyContent: 'space-between', width: '100%' }}>
				<Text size="caption" sx={{ display: 'flex', alignItems: 'center' }}>
					<Icon icon={iconLock} mr={4} />
					<Translate value="corgi.movement.show.petition_card.admin" />
				</Text>
				{userCanRemoveThePetition && <RemoveOption onDelete={onDelete} />}
			</Flex>
		);
	}
	if (weeklySignatureCount >= WEEKLY_SIGNATURE_THRESHOLD) {
		return (
			<Flex sx={{ justifyContent: 'space-between', width: '100%' }}>
				<Text size="caption" sx={{ display: 'flex', alignItems: 'center' }}>
					<Icon icon={iconGroup} color="neutral-grey700" mr={4} />
					<TranslatePlural
						value="fe.components.folksonomy.new_supporters"
						count={weeklySignatureCount}
						replacements={{ num_supporters: localizeNumber(weeklySignatureCount) }}
					/>
				</Text>
				{userCanRemoveThePetition && <RemoveOption onDelete={onDelete} />}
			</Flex>
		);
	}
	if (isPetitionOwner) {
		return (
			<Flex sx={{ justifyContent: 'space-between', width: '100%' }}>
				<Text size="caption" sx={{ display: 'flex', alignItems: 'center' }}>
					<Icon icon={iconDraw} mr={4} />
					<Translate value="fe.components.folksonomy.petition_started" />
				</Text>
				{userCanRemoveThePetition && <RemoveOption onDelete={onDelete} />}
			</Flex>
		);
	}
	return (
		<Flex sx={{ justifyContent: 'space-between', width: '100%' }}>
			<Text size="caption" sx={{ display: 'flex', alignItems: 'center' }}>
				<Icon icon={iconDraw} mr={4} />
				<Translate
					value="fe.components.folksonomy.started"
					replacements={{ time_ago: localizeRelativeTime(now, publishedAt) }}
				/>
			</Text>
		</Flex>
	);
}
